import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle } from "lucide-react";
import { errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { useEditor } from "./useEditorState";
import { AutoConnect, Pending } from "./ModulesAuto";

const KIND = { booking: "Prenotazione", shop: "Carrello / checkout", form: "Modulo contatto" };
const MOD = { booking: "le prenotazioni online", shop: "il negozio online", form: "un modulo del sito (scheda Richieste del sito)" };
const DONE = { widget: "Widget reale collegato al posto della sezione", slot: "Spazio modulo inserito (non cliccabile)", hidden: "Sezione nascosta", cart_bridge: "Il carrello del menu apre il carrello reale", cart_redirect: "La pagina ora apre il carrello reale" };

function Notes({ x, mods, bound }) {
  const moved = mods[x.kind] && bound[x.kind] && bound[x.kind].section_id !== x.section_id;
  return (
    <>
      {moved && <p className="text-[11px] text-amber-800" data-testid={`sim-move-note-${x.section_id}`}>Il widget reale è già in «{bound[x.kind].label || "un'altra sezione"}»: sostituendo qui viene spostato (resta un solo widget, quella sezione viene nascosta).</p>}
      {x.last && <p className="text-[11px] text-amber-800" data-testid={`sim-last-note-${x.section_id}`}>È l'unica sezione visibile della pagina: nascondendola la pagina resta vuota{x.kind === "shop" ? " (una pagina carrello/checkout aprirà invece il carrello reale)" : ""}.</p>}
      {!mods[x.kind] && <p className="text-[11px] text-slate-400">Attiva {MOD[x.kind]} per mostrare subito il widget reale; intanto resta uno spazio non cliccabile.</p>}
    </>
  );
}

function Issue({ x, mods, bound, run, site }) {
  const comp = !!x.component;
  const bridge = comp && x.kind === "shop" && /cart|carrello|drawer|basket/i.test(x.component);
  return (
    <li className="space-y-2 rounded-lg border border-amber-200 bg-white p-3" data-testid={`sim-issue-${x.section_id || x.component}`}>
      <p className="text-sm font-semibold text-slate-800">{comp ? `Componente «${x.component}»` : `${x.page} · ${x.section}`}</p>
      <p className="text-xs text-slate-500">{KIND[x.kind]}: {x.reason}</p>
      {comp ? (bridge ? <Button size="sm" onClick={() => run(x, "replace")} data-testid={`sim-bridge-${x.component}`}>Collega al carrello reale</Button>
        : <p className="text-xs text-slate-500">Da rivedere a mano nel codice del componente (i pulsanti devono portare alla pagina del modulo reale).</p>)
        : <><div className="flex flex-wrap gap-2">
          <Button size="sm" onClick={() => run(x, "replace")} data-testid={`sim-replace-${x.section_id}`}>{mods[x.kind] ? "Sostituisci con widget reale" : "Sostituisci con spazio modulo"}</Button>
          <Button size="sm" variant="outline" onClick={() => run(x, "hide")} data-testid={`sim-hide-${x.section_id}`}>Nascondi sezione</Button></div>
          <Notes x={x} mods={mods} bound={bound} />
          {site && !mods[x.kind] && x.kind !== "form" && <p className="text-[11px] text-slate-500" data-testid={`sim-auto-hint-${x.section_id}`}>Usa «Collega automaticamente» qui sopra: attiva {MOD[x.kind]} e collega questa sezione.</p>}</>}
    </li>
  );
}

function EmptyPage({ p, run }) {
  return (
    <li className="space-y-2 rounded-lg border border-slate-200 bg-white p-3" data-testid={`sim-empty-${p.page_id}`}>
      <p className="text-sm font-semibold text-slate-800">Pagina «{p.page}» vuota</p>
      {p.cart ? <><p className="text-xs text-slate-500">Tutte le sezioni sono nascoste: i link a questa pagina (menu, piè di pagina) non mostrano nulla.</p>
        <Button size="sm" onClick={() => run({ kind: "shop", page_id: p.page_id }, "cart_redirect")} data-testid={`sim-redirect-${p.page_id}`}>Apri il carrello reale da questa pagina</Button></>
        : <p className="text-xs text-slate-500">Tutte le sezioni sono nascoste: ripristinane una dalla cronologia o aggiungi contenuti dall'editor.</p>}
    </li>
  );
}

function SlotIssue({ x, d, run, ed }) {
  const site = d.sites?.[0];
  const ready = x.kind === "form" ? !!site : d.modules[x.kind];
  return (
    <li className="space-y-2 rounded-lg border border-red-200 bg-white p-3" data-testid={`sim-slot-${x.section_id}`}>
      <p className="text-sm font-semibold text-slate-800">{x.page} · {x.section}</p>
      <p className="text-xs text-slate-500">{KIND[x.kind]}: spazio modulo non collegato, blocca la pubblicazione.</p>
      {!site && <p className="text-xs text-amber-800">Il progetto non ha ancora un cliente: assegnalo dal nome cliente in alto, poi crea il modulo reale.</p>}
      {site && !ready && <p className="text-xs text-slate-600">Si collega con «Collega automaticamente» qui sopra (attiva {MOD[x.kind]} del cliente). Se non serve, nascondi la sezione.</p>}
      <div className="flex flex-wrap gap-2">
        {ready && <Button size="sm" onClick={() => run(x, "replace")} data-testid={`sim-slot-connect-${x.section_id}`}>{x.kind === "form" ? "Crea e collega il modulo reale" : "Collega il widget reale"}</Button>}
        <Button size="sm" variant="outline" onClick={() => { ed.setPageId(x.page_id); ed.setSelection({ kind: "section", id: x.section_id }); }} data-testid={`sim-slot-select-${x.section_id}`}>Mostra sezione</Button>
        <Button size="sm" variant="outline" onClick={() => run(x, "hide")} data-testid={`sim-slot-hide-${x.section_id}`}>Nascondi sezione</Button>
      </div>
    </li>
  );
}

export default function SimulatedModules() {
  const ed = useEditor();
  const [d, setD] = useState(null);
  const [open, setOpen] = useState(false);
  const load = useCallback(() => ed.api.get(`/projects/${ed.id}/module-check`).then(({ data }) => setD(data)).catch(() => setD(null)), [ed.api, ed.id]);
  useEffect(() => { load(); const h = () => load(); const o = () => { load(); setOpen(true); }; window.addEventListener("adm:project-changed", h); window.addEventListener("adm:open-modules", o);
    return () => { window.removeEventListener("adm:project-changed", h); window.removeEventListener("adm:open-modules", o); }; }, [load]);
  const run = async (x, action) => {
    try {
      await ed.save();
      const { data } = await ed.api.post(`/projects/${ed.id}/module-fix`, { kind: x.kind, action, page_id: x.page_id, section_id: x.section_id, component: x.component });
      toast.success(DONE[data.mode] + (data.page_empty ? ": la pagina è rimasta vuota" : ""));
      await ed.load(); load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const empty = (d?.empty_pages || []).filter((p) => p.cart);
  const loaded = !!d;
  useEffect(() => { if (loaded && new URLSearchParams(window.location.search).get("moduli")) setOpen(true); }, [loaded]);
  const pend = (d?.pending?.services?.length || 0) + (d?.pending?.products?.length || 0);
  const n = (d?.issues?.length || 0) + (d?.slots?.length || 0) + empty.length + pend;
  if (!n) return null;
  return (
    <>
      <button type="button" onClick={() => setOpen(true)} className="absolute left-3 top-3 z-20 flex items-center gap-1.5 rounded-full border border-amber-300 bg-amber-50 px-3 py-1.5 text-xs font-medium text-amber-900 shadow-sm" data-testid="sim-banner">
        <AlertTriangle size={13} />{n - pend ? `${n - pend} ${n - pend === 1 ? "modulo da collegare" : "moduli da collegare"}` : ""}{n - pend && pend ? " · " : ""}{pend ? `${pend} da confermare` : ""}</button>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent className="w-full overflow-y-auto sm:max-w-lg" data-testid="sim-sheet">
          <SheetTitle>Moduli del sito</SheetTitle>
          <p className="mt-1 text-xs text-slate-500">Moduli contatto, prenotazioni e negozio del sito diventano quelli reali del cliente (richieste, calendario e ordini nel CRM). Prima di ogni modifica viene salvata una versione.</p>
          <div className="mt-4 space-y-3"><AutoConnect ed={ed} onDone={load} /><Pending ed={ed} pending={d.pending} onDone={load} /></div>
          <ul className="mt-4 space-y-2">
            {(d.slots || []).map((x) => <SlotIssue key={x.section_id} x={x} d={d} run={run} ed={ed} />)}
            {d.issues.map((x) => <Issue key={(x.section_id || x.component) + x.kind} x={x} mods={d.modules} bound={d.bound || {}} run={run} site={d.sites?.[0]} />)}
            {(d.empty_pages || []).map((p) => <EmptyPage key={p.page_id} p={p} run={run} />)}
          </ul>
        </SheetContent>
      </Sheet>
    </>
  );
}
